/**
 * Work agreements (domain-model-transition P6's REST resource, P7a's form — worklist W5).
 *
 * An agreement is the legal instrument that says these hours AVERAGE before overtime is computed.
 * It is not a rotation and not a schedule: `bid-project-shift-design.md` keeps those separate, and
 * the same agreement can cover successive rotations.
 *
 * READS are permish {1,2} on the server and WRITES are {1} — an asymmetry with a reason, recorded
 * in the endpoint's own comment: `?scope_user_id=<colleague>` would otherwise answer whether a
 * named individual holds an averaging arrangement. The form gates its controls on {1}, matching
 * the write wall rather than the read one.
 */
import HttpService from './HttpService';
import type { ItemResponse, ItemsResponse } from '@frontend/Models/Http/HttpModels';

const http = new HttpService();

const pathWorkAgreements = 'api/work-agreements';
const pathWorkAgreementId = (id: string) => `${pathWorkAgreements}/${id}`;

export interface WorkAgreement {
  id: string;
  scope_type: 'user' | 'matter';
  scope_user_id: string | null;
  scope_matterid: string | null;
  jurisdiction_id: string;
  agreement_type: string;
  averaging_period_weeks: number | null;
  /** Nullable with deliberately NO default (P6's D27): "no schedule recorded" and "scheduled
   *  eight" must stay distinguishable, because an unrecorded one classifies a scheduled 10-hour
   *  day as 8 + 2 — silently wrong pay rather than missing data. */
  scheduled_daily_hours: string | null;
  effective_from: string;
  /** The retirement verb. Closing an agreement preserves why hours averaged; deleting it does
   *  not, which is why the server 409s a delete once time falls inside the window. */
  effective_to: string | null;
}

export interface CreateWorkAgreementBody {
  scope_type: 'user' | 'matter';
  scope_user_id?: string;
  scope_matterid?: string;
  jurisdiction_id: string;
  agreement_type: string;
  averaging_period_weeks?: number;
  scheduled_daily_hours?: number;
  effective_from: string;
  effective_to?: string;
}

export class WorkAgreementService {
  static async list(): Promise<WorkAgreement[]> {
    const response = await http.get<ItemsResponse<WorkAgreement>>(pathWorkAgreements);
    return response.data.items;
  }

  static async create(body: CreateWorkAgreementBody): Promise<WorkAgreement> {
    const response = await http.post<ItemResponse<WorkAgreement>>(pathWorkAgreements, body);
    return response.data.item;
  }

  /** CLOSE, never delete — write an `effective_to`. The server's PUT uses three-valued
   *  semantics, so an omitted field keeps its stored value. */
  static async close(id: string, effective_to: string): Promise<WorkAgreement> {
    const response = await http.put<ItemResponse<WorkAgreement>>(
      pathWorkAgreementId(id), { effective_to });
    return response.data.item;
  }
}

export default WorkAgreementService;
